// /Users/pramod/Desktop/school-erp/frontend/src/Tenant/TenantWebsite/sections/Recruiters.js

import React from 'react';
import { FiBriefcase } from 'react-icons/fi';
import buildResolvedFile from '../utils/buildResolvedFile';
import useBrandColors from '../utils/useBrandColors';

const getImage = (item, apiBaseURL) => {
  const candidate = item.logo || item.image || item.imageUrl || '';
  return buildResolvedFile(candidate, apiBaseURL).resolvedSrc || '';
};

const RecruiterCard = ({ item, apiBaseURL }) => {
  const logoSrc = getImage(item, apiBaseURL);
  const { primary, secondary, fontHeading } = useBrandColors();

  return (
    <div 
      className="group bg-white rounded-2xl p-6 text-center border transition-all duration-500 hover:shadow-xl hover:-translate-y-1 flex-shrink-0 w-56 mx-3"
      style={{ borderColor: `${primary}15` }}
    >
      <div className="flex items-center justify-center h-20 mb-3">
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={item.name}
            className="h-12 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ 
              background: `linear-gradient(135deg, ${primary}15, ${secondary}15)`,
              color: secondary
            }}
          >
            <FiBriefcase className="w-6 h-6" />
          </div>
        )}
      </div>

      <h4 
        className="font-medium text-sm mb-1 truncate"
        style={{ 
          color: '#1F2937',
          fontFamily: fontHeading,
        }}
      >
        {item.name}
      </h4>

      {item.category && (
        <p className="text-xs mb-2 truncate" style={{ color: '#9CA3AF' }}>
          {item.category}
        </p>
      )}

      {item.hires > 0 && (
        <p className="text-xs" style={{ color: '#6B7280' }}>
          {item.hires} hires
        </p>
      )}
    </div>
  );
};

const Recruiters = ({ recruiters, apiBaseURL = '' }) => {
  const { sectionTitle, sectionSubtitle, items = [] } = recruiters || {};
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();

  if (!items || items.length === 0) return null;

  // Duplicated for an uninterrupted loop
  const duplicatedItems = [...items, ...items];

  return (
    <section id="recruiters" className="py-16 md:py-24 bg-white overflow-hidden">
      {/* Updated keyframes below to achieve right-to-left transition */}
      <style>{`
        @keyframes marqueeRightToLeft {
          0% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .animate-marquee-horizontal {
          display: flex;
          width: max-content;
          animation: marqueeRightToLeft 30s linear infinite;
        }
        .animate-marquee-horizontal:hover {
          animation-play-state: paused;
        }
      `}</style>

      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {(sectionTitle || sectionSubtitle) && (
            <div className="text-center mb-12 md:mb-16">
              <span 
                className="inline-block text-xs font-light tracking-[0.2em] uppercase mb-3"
                style={{ color: secondary }}
              >
                Recruiters
              </span>
              {sectionTitle && (
                <h2 
                  className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3"
                  style={{ 
                    color: primary,
                    fontFamily: fontHeading,
                  }}
                >
                  {sectionTitle}
                </h2>
              )}
              {sectionSubtitle && (
                <p 
                  className="text-lg font-light max-w-2xl mx-auto"
                  style={{ 
                    color: '#6B7280',
                    fontFamily: fontBody,
                  }}
                >
                  {sectionSubtitle}
                </p>
              )}
              <div className="mt-6 flex justify-center">
                <div 
                  className="h-1 w-20 rounded-full" 
                  style={{ 
                    background: `linear-gradient(90deg, ${primary}, ${secondary})` 
                  }} 
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="w-full overflow-hidden relative">
        {/* Sleek edge fades to cleanly mask the coming/going cards */}
        <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
        
        <div className="animate-marquee-horizontal py-4">
          {duplicatedItems.map((item, index) => (
            <RecruiterCard 
              key={`${item._id || item.id || item.name}-${index}`} 
              item={item} 
              apiBaseURL={apiBaseURL}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Recruiters;